import { useGetAccountPaymentMethods } from '@cocoonspace/sdk-js/domains/account/account'
import type { Space } from '@cocoonspace/sdk-js/types/space'
import { useAuth } from '@cocoonspace/shared/domains/auth/hooks/use-auth.hook'
import { useAuthDialogStore } from '@cocoonspace/shared/domains/auth/stores/use-auth-dialog-store.hook'
import { dateTimeUtils } from '@cocoonspace/shared/utils/date-time-utils'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { Button } from '~/components/ui/button'
import { Card } from '~/components/ui/card'
import { Form, FormField } from '~/components/ui/form'
import { FactuInfosRecap } from '~/domains/account/components/factus-info-recap'
import { PersoInfosRecap } from '~/domains/account/components/perso-infos-recap'
import { useBookingVisitMutation } from '~/domains/booking/hooks/use-booking-visit-mutation.hook'
import { EventDateBadge } from '~/domains/events/components/atoms/event-date-badge'
import { PaymentMethodSelector } from '~/domains/payments/components/payment-method-selector'
import { useUserContext } from '~/providers/user-provider'
import { useBookingVisitStore } from '../space-visit.store'

const formValuesSchema = z.object({
	day: z.string(),
	start: z.string(),
	end: z.string(),
	paymentMethod: z.string(),
})

type FormValues = z.infer<typeof formValuesSchema>

export const PaymentStepAuth = ({
	space,
}: {
	space: Space
}) => {
	const router = useRouter()
	const { t } = useTranslation()
	const { isInfoFactuIncomplete, isInfosPersoIncomplete, isIncompleteProfile } =
		useUserContext()

	const visitBookingMutation = useBookingVisitMutation()

	const defaultPmQuery = useGetAccountPaymentMethods({
		query: { select: (data) => data.data.find((pm) => pm.default) },
	})

	const day = useBookingVisitStore.use.day()
	const startTime = useBookingVisitStore.use.startTime()

	const form = useForm<FormValues>({
		resolver: zodResolver(formValuesSchema),
		values: {
			day,
			start: startTime || '',
			end: dateTimeUtils.utils.addDurationToTime(startTime!, 30),
			paymentMethod: defaultPmQuery.data?.id || '',
		},
	})

	const formValues = form.watch()

	const onSubmit = ({ start, end, day, paymentMethod }: FormValues) => {
		const query = visitBookingMutation
			.mutateAsync({ day, paymentMethod, start, end, space: space.id })
			.then((data) => {
				router.push({
					pathname: '/event/[eventId]',
					query: { eventId: data.event.id },
				})
			})

		toast.promise(query, {
			loading: t('actions:loading.btnLabel'),
			success: t('space:visit.dialog.payment.success'),
			error: (err) => {
				const errorCode = err?.response?.data?.errors?.[0].code as
					| string
					| undefined

				return t([
					`errors:context.booking.${errorCode}`,
					`errors:api.${errorCode}`,
					'errors:generic',
				])
			},
		})
	}

	return (
		<Form {...form}>
			<form
				onSubmit={form.handleSubmit(onSubmit)}
				noValidate
			>
				<div className='flex max-w-[350px] flex-col gap-4'>
					<Card className='flex items-stretch divide-x overflow-hidden border'>
						<EventDateBadge
							date={day}
							altStyle
						/>

						<div className='flex flex-1 flex-col justify-center px-4'>
							<p className='truncate font-bold text-lg'>{space.title}</p>

							<div className='flex items-center'>
								{t('common:fromTo', {
									start: formValues.start
										?.replace(':', 'h')
										.replace('h00', 'h'),
									end: formValues.end?.replace(':', 'h').replace('h00', 'h'),
								})}
							</div>
						</div>
					</Card>

					{isInfosPersoIncomplete && (
						<Card className='border p-4'>
							<PersoInfosRecap />
						</Card>
					)}

					{!isInfosPersoIncomplete && isInfoFactuIncomplete && (
						<Card className='border p-4'>
							<FactuInfosRecap />
						</Card>
					)}

					<div className='flex flex-col gap-2'>
						<div className='font-bold'>
							{t('space:visit.dialog.payment.myPaymentMethod')}
						</div>

						<FormField
							name='paymentMethod'
							control={form.control}
							render={({ field }) => (
								<PaymentMethodSelector
									value={field.value}
									onChange={field.onChange}
								/>
							)}
						/>
					</div>

					<Button
						type='submit'
						disabled={visitBookingMutation.isLoading || isIncompleteProfile}
					>
						{t('actions:validate.btnLabel')}
					</Button>
				</div>
			</form>
		</Form>
	)
}

export const PaymentStep = ({
	space,
}: {
	space: Space
}) => {
	const { t } = useTranslation()
	const { isAuth } = useAuth()
	const authStore = useAuthDialogStore()

	useEffect(() => {
		if (!isAuth) {
			authStore.openDialog()
		}
	}, [isAuth])

	if (!isAuth) {
		return (
			<div className='flex flex-col gap-4'>
				{t('errors:mustBeAuthToBook')}

				<Button onClick={() => authStore.openDialog()}>
					{t('actions:login.btnLabel')}
				</Button>
			</div>
		)
	}

	return <PaymentStepAuth space={space} />
}
